import { Icon, Status, Toggle } from '@atlas/ui-kit'
import { type ReactNode, useState } from 'react'

/**
 * One white card of the step, as the Figma draws each section: a 24px heading, the
 * "Requires Action" status while a field the Send Task guard needs is empty, and on the right
 * either a control (a section's Yes toggle) or, for a section without one, the arrow that
 * folds it.
 */
export function DetailsCard({
	title,
	requiresAction = false,
	action,
	children,
}: {
	title: string
	requiresAction?: boolean
	action?: ReactNode
	children: ReactNode
}) {
	const [isExpanded, setIsExpanded] = useState(true)
	return (
		<section aria-label={title} className="flex flex-col gap-6 rounded-atlas-lg bg-atlas-light p-6 font-atlas-sans">
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-center gap-2">
					<h2 className="text-atlas-h3 text-atlas-dark">{title}</h2>
					{requiresAction ? <Status tone="warning" label="Requires Action" /> : null}
				</div>
				{action ?? (
					<button
						type="button"
						aria-label={title}
						aria-expanded={isExpanded}
						className="cursor-pointer rounded-atlas-sm"
						onClick={() => setIsExpanded(!isExpanded)}
					>
						<Icon name="arrow-up" />
					</button>
				)}
			</div>
			{isExpanded || action ? children : null}
		</section>
	)
}

/** A field's caption, with the asterisk of a field the Send Task guard requires. */
export function FieldCaption({ label, isRequired = false }: { label: string; isRequired?: boolean }) {
	return (
		<span className="text-atlas-label text-atlas-light-dark">
			{label}
			{isRequired ? ' *' : ''}
		</span>
	)
}

/** The "Yes" switch a section opens with, such as "Is this part of a campaign?". */
export function YesToggle({
	label,
	isSelected,
	onChange,
	isDisabled,
}: {
	/** The accessible name: the question the switch answers. */
	label: string
	isSelected: boolean
	onChange?: (isSelected: boolean) => void
	isDisabled?: boolean
}) {
	return (
		<div className="flex items-center gap-2">
			<span aria-hidden="true" className="text-atlas-body-semibold text-atlas-dark">
				Yes
			</span>
			<Toggle aria-label={label} isSelected={isSelected} onChange={onChange} isDisabled={isDisabled || !onChange} />
		</div>
	)
}

/** The multi-line text field, in the kit's filled look, with a copy action once it holds text. */
export function TextArea({
	label,
	value,
	onChange,
	onCopy,
	isRequired,
	placeholder,
}: {
	label: string
	value: string
	onChange?: (value: string) => void
	onCopy?: (value: string) => void
	isRequired?: boolean
	placeholder?: string
}) {
	return (
		<div className="flex flex-col gap-1">
			<FieldCaption label={label} isRequired={isRequired} />
			<div className="relative">
				<textarea
					aria-label={label}
					className="min-h-32 w-full rounded-atlas-md bg-atlas-background py-2 pr-12 pl-4 font-atlas-sans text-atlas-body text-atlas-dark outline-none focus-visible:inset-ring-2 focus-visible:inset-ring-atlas-accent"
					value={value}
					placeholder={placeholder}
					readOnly={!onChange}
					aria-required={isRequired || undefined}
					onChange={(event) => onChange?.(event.target.value)}
				/>
				{onCopy && value.trim() ? (
					<button
						type="button"
						aria-label={`Copy ${label}`}
						className="absolute top-3 right-3 cursor-pointer rounded-atlas-sm"
						onClick={() => onCopy(value)}
					>
						<Icon name="copy-01" />
					</button>
				) : null}
			</div>
		</div>
	)
}
